/**
 * Contatos — Provider + hook compartilhados (fonte única de verdade).
 * Todas as telas (Chat, CRM, Kanban) devem usar useContacts() dentro de ContactsProvider.
 */
import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { supabase } from '../../../lib/supabase';
import { Contact } from '../../../types';
import { useOrganization } from '../../../contexts/OrganizationContext';
import { createModuleLogger } from '../../../utils/logger';
import { contactPhoneKey } from '../../../utils/phoneUtils';
import {
    CONTACT_ROW_SELECT,
    ERR_NO_ORG,
    duplicatePhoneError,
    findDuplicateByPhoneKey,
    isContactsSessionFailure,
    mapContactError,
    mapContactRow,
    upsertContactSorted,
} from './contactsHelpers';

const log = createModuleLogger('[Contacts]');

const CONTACTS_PAGE_SIZE = 1000;

interface ContactsContextData {
    contacts: Contact[];
    loading: boolean;
    error: string | null;
    refreshContacts: () => Promise<void>;
    createContact: (contactData: Omit<Contact, 'id'>) => Promise<Contact>;
    updateContact: (id: string, contactData: Partial<Contact>) => Promise<Contact>;
    deleteContact: (id: string) => Promise<void>;
}

const ContactsContext = createContext<ContactsContextData | undefined>(undefined);

export const ContactsProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const { currentOrganization } = useOrganization();
    const [contacts, setContacts] = useState<Contact[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const fetchContacts = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);

            if (!currentOrganization) {
                log.warn('No organization selected');
                setContacts([]);
                return;
            }

            const allRows: Contact[] = [];
            let offset = 0;

            while (true) {
                const { data, error: fetchError } = await supabase
                    .from('contacts')
                    .select(CONTACT_ROW_SELECT)
                    .eq('organization_id', currentOrganization.id)
                    .order('name')
                    .range(offset, offset + CONTACTS_PAGE_SIZE - 1);

                if (fetchError) throw fetchError;

                const batch = (data || []).map(mapContactRow);
                allRows.push(...batch);
                if (batch.length < CONTACTS_PAGE_SIZE) break;
                offset += CONTACTS_PAGE_SIZE;
            }

            setContacts(allRows);
        } catch (err: unknown) {
            // Sessão já inválida (401 / RLS auth.user_org_ids) — AuthContext redireciona;
            // não poluir console como se fosse bug de contacts.
            if (isContactsSessionFailure(err)) {
                setContacts([]);
                setError(null);
                return;
            }
            const mapped = mapContactError(err as { code?: string; message?: string });
            log.error('Error fetching contacts:', err);
            setError(mapped || (err instanceof Error ? err.message : 'Erro ao carregar contatos'));
        } finally {
            setLoading(false);
        }
    }, [currentOrganization?.id]);

    const createContact = useCallback(async (contactData: Omit<Contact, 'id'>) => {
        if (!currentOrganization) throw new Error(ERR_NO_ORG);

        const name = (contactData.name ?? '').trim();
        if (!name) throw new Error('Informe o nome do contato.');

        const { email, whatsapp, phone, company, assigned_to, tags, origin_id } = contactData;
        const phoneKey = contactPhoneKey(phone || whatsapp);

        if (phoneKey) {
            const duplicate = await findDuplicateByPhoneKey(currentOrganization.id, phoneKey);
            if (duplicate) {
                const { data: existing } = await supabase
                    .from('contacts')
                    .select(CONTACT_ROW_SELECT)
                    .eq('id', duplicate.id)
                    .single();

                if (existing) {
                    setContacts((prev) => upsertContactSorted(prev, mapContactRow(existing)));
                }
                throw duplicatePhoneError(duplicate.name);
            }
        }

        const { data, error: insertError } = await supabase
            .from('contacts')
            .insert([{
                name,
                email: (email ?? '').trim() || null,
                phone: phoneKey,
                whatsapp: phoneKey,
                company: (company ?? '').trim() || null,
                organization_id: currentOrganization.id,
                assigned_to: assigned_to || null,
                tags: tags || null,
                origin_id: origin_id || null,
            }])
            .select(CONTACT_ROW_SELECT)
            .single();

        if (insertError) throw new Error(mapContactError(insertError));

        const mappedContact = mapContactRow(data);
        setContacts((prev) => upsertContactSorted(prev, mappedContact));
        return mappedContact;
    }, [currentOrganization?.id]);

    const updateContact = useCallback(async (id: string, contactData: Partial<Contact>) => {
        if (!currentOrganization) throw new Error(ERR_NO_ORG);

        const {
            name,
            email,
            whatsapp,
            phone,
            company,
            assigned_to,
            assignedTo,
            tags,
            origin_id,
        } = contactData;

        const updatePayload: Record<string, unknown> = {};
        if (name !== undefined) updatePayload.name = name;
        if (email !== undefined) updatePayload.email = email || null;
        if (company !== undefined) updatePayload.company = company || null;
        if (tags !== undefined) updatePayload.tags = tags || null;
        if (origin_id !== undefined) updatePayload.origin_id = origin_id || null;
        if (assigned_to !== undefined) updatePayload.assigned_to = assigned_to || null;
        else if (assignedTo !== undefined) updatePayload.assigned_to = assignedTo || null;

        if (whatsapp !== undefined || phone !== undefined) {
            const phoneKey = contactPhoneKey(phone || whatsapp);
            if (phoneKey) {
                const duplicate = await findDuplicateByPhoneKey(currentOrganization.id, phoneKey, id);
                if (duplicate) throw duplicatePhoneError(duplicate.name);
            }
            updatePayload.phone = phoneKey;
            updatePayload.whatsapp = phoneKey;
        }

        const { data, error: updateError } = await supabase
            .from('contacts')
            .update(updatePayload)
            .eq('id', id)
            .select(CONTACT_ROW_SELECT)
            .single();

        if (updateError) throw new Error(mapContactError(updateError));

        const mappedContact = mapContactRow(data);
        setContacts((prev) => prev.map((c) => (c.id === id ? mappedContact : c)));
        return mappedContact;
    }, [currentOrganization?.id]);

    const deleteContact = useCallback(async (id: string) => {
        const { error: deleteError } = await supabase.from('contacts').delete().eq('id', id);
        if (deleteError) throw deleteError;
        setContacts((prev) => prev.filter((c) => c.id !== id));
    }, []);

    useEffect(() => {
        if (currentOrganization) {
            fetchContacts();
        } else {
            setLoading(false);
            setContacts([]);
        }
    }, [currentOrganization?.id, fetchContacts]);

    const value: ContactsContextData = {
        contacts,
        loading,
        error,
        refreshContacts: fetchContacts,
        createContact,
        updateContact,
        deleteContact,
    };

    return React.createElement(ContactsContext.Provider, { value }, children);
};

export const useContacts = (): ContactsContextData => {
    const context = useContext(ContactsContext);
    if (!context) {
        throw new Error('useContacts must be used within ContactsProvider');
    }
    return context;
};
